import type { UiMessage } from "./chatData";

const GROUP_MS = 5 * 60_000;

export type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "unread"; key: string }
  | { kind: "message"; key: string; message: UiMessage; first: boolean };

export function dayLabel(d: Date, now = new Date()): string {
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
}

export function timeLabel(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * Day separators, a "New" marker before the first unread message from someone else, and grouping: consecutive
 * messages from one person within five minutes share one header, unless a message is a reply.
 */
export function buildRows(messages: UiMessage[], opts: { me: string; lastRead: number; now?: Date }): Row[] {
  const rows: Row[] = [];
  let prev: UiMessage | undefined;
  let unreadPlaced = false;
  for (const m of messages) {
    const at = new Date(m.createdAt);
    const label = dayLabel(at, opts.now);
    const newDay = !prev || dayLabel(new Date(prev.createdAt), opts.now) !== label;
    if (newDay) rows.push({ kind: "day", key: `d${m.id}`, label });
    const isUnread = !unreadPlaced && m.id > opts.lastRead && m.id > 0 && m.author !== opts.me;
    if (isUnread) {
      rows.push({ kind: "unread", key: "unread" });
      unreadPlaced = true;
    }
    const first = newDay || isUnread || !prev || prev.author !== m.author || m.replyTo !== null || at.getTime() - new Date(prev.createdAt).getTime() > GROUP_MS;
    rows.push({ kind: "message", key: m.clientId ?? String(m.id), message: m, first });
    prev = m;
  }
  return rows;
}

export type Segment = { text: string; href?: string };

/** Splits text into plain and link segments. Only http(s) links become links; trailing punctuation stays text. */
export function linkify(text: string): Segment[] {
  const out: Segment[] = [];
  const re = /\bhttps?:\/\/[^\s<>"]+/gi;
  let last = 0;
  for (const m of text.matchAll(re)) {
    let url = m[0];
    const trail = /[.,;:!?)\]]+$/.exec(url)?.[0] ?? "";
    if (trail) url = url.slice(0, -trail.length);
    const start = m.index ?? 0;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ text: url, href: url });
    last = start + url.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function typingText(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return `${names[0]} is typing`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
  return "Several people are typing";
}
